import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { Result } from '@cplug/frontend-core/utils'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { usePaginatedSearch } from '~/shared/composables/usePaginatedSearch'
import useCompanyStore, { useCompanyStoreForHeader } from '~/domain/dashboard/company/stores/useCompanyStore'
import { isSessionRestoreComplete, restoreSessionOnce } from '~/domain/shared/authentication/composables/useSessionRestoreState'
import { formatCompanySelectLabel } from '~/domain/shared/company/composables/formatCompanySelectLabel'
import { useTranslation } from '~/domain/shared/navigation/components/BaseHeaderRightSide.i18n'
import {
  clearHeaderCompanySelectInitialLoadPromise,
  getHeaderCompanySelectInitialLoadPromise,
  setHeaderCompanySelectInitialLoadPromise
} from '~/domain/shared/navigation/composables/useHeaderCompanySelectSession'
import type { Company } from '~/domain/shared/company/models/Company'

export type CompanySelectOption = {
  label: string
  value?: number
}

type HeaderCompanySelectSearch = {
  selectOptions: ComputedRef<CompanySelectOption[]>
  isRemoteSearchLoading: ComputedRef<boolean>
  isMultiPage: Ref<boolean>
  companies: Ref<Company[] | undefined>
  pageMeta: Ref<{ total?: number, lastPage?: number, page?: number, perPage?: number } | undefined>
  onFilter: (value: string) => void
  onDropdownHide: () => Promise<boolean>
  ensureInitialLoad: () => Promise<boolean>
  findCompanyById: (id: number | undefined) => Company | undefined
}

const PAGE = 1
const PER_PAGE = 100
const REMOTE_DEBOUNCE_MS = 300

function mapCompaniesToOptions (
  companies: Company[] | undefined,
  headquartersLabel: string
): CompanySelectOption[] {
  return (companies ?? []).map(company => ({
    label: formatCompanySelectLabel(company, headquartersLabel),
    value: company.id
  }))
}

export function useHeaderCompanySelectSearch (): HeaderCompanySelectSearch {
  const { t } = useTranslation()
  const headquartersLabel = t('Headquarters')
  const authStore = useAuthenticationStore()
  const { companies: authCompanies } = storeToRefs(authStore)
  const selectedCompanyId = computed(() => authCompanies.value[0]?.id)
  const headerStore = useCompanyStoreForHeader()
  const { pageList, pageMeta } = storeToRefs(headerStore)

  const paginatedSearch = usePaginatedSearch<Company>({
    modelStoreInstance: headerStore,
    itemValue: 'id',
    itemLabel: 'name',
    searchField: 'name',
    perPage: PER_PAGE,
    debounceDelay: REMOTE_DEBOUNCE_MS,
    persistent: true
  })

  const isMultiPage = ref(false)
  const localCompanies = ref<Company[]>([])

  const isRemoteSearchLoading = computed(() => paginatedSearch.loading.value)

  function findCompanyById (id: number | undefined): Company | undefined {
    if (id == null) {
      return undefined
    }

    const fromLocal = localCompanies.value.find(company => company.id === id)
    if (fromLocal != null) {
      return fromLocal
    }

    const fromPage = pageList.value?.find(company => company.id === id)
    if (fromPage != null) {
      return fromPage
    }

    const fromSearchItems = (paginatedSearch.currentItems.value as Company[]).find(company => company.id === id)
    if (fromSearchItems != null) {
      return fromSearchItems
    }

    return authCompanies.value.find(company => company.id === id)
  }

  function withSelectedCompanyOption (
    options: CompanySelectOption[],
    selectedCompanyId: number | undefined
  ): CompanySelectOption[] {
    if (selectedCompanyId == null) {
      return options
    }

    if (options.some(option => option.value === selectedCompanyId)) {
      return options
    }

    const selectedCompany = findCompanyById(selectedCompanyId)
    if (selectedCompany?.id == null) {
      return options
    }

    return [{
      label: formatCompanySelectLabel(selectedCompany, headquartersLabel),
      value: selectedCompany.id
    }, ...options]
  }

  const selectOptions = computed(() => {
    const searchText = paginatedSearch.searchText.value?.trim() ?? ''

    const mappedOptions = searchText.length > 0
      ? mapCompaniesToOptions(paginatedSearch.currentItems.value as Company[], headquartersLabel)
      : mapCompaniesToOptions(
        localCompanies.value.length > 0 ? localCompanies.value : pageList.value,
        headquartersLabel
      )

    return withSelectedCompanyOption(mappedOptions, selectedCompanyId.value)
  })

  function syncLocalCompaniesFromStore (): void {
    localCompanies.value = [...(pageList.value ?? [])]
  }

  function syncPaginatedSearchFromStore (): void {
    paginatedSearch.items.value = [...(pageList.value ?? [])]
    paginatedSearch.firstLoadDone.value = true
  }

  function tryHydrateFromMainCompanyStore (): boolean {
    const sessionCompany = authCompanies.value[0]
    const sessionCompanyId = sessionCompany?.id
    if (sessionCompanyId == null) {
      return false
    }

    const mainStore = useCompanyStore()
    const listedCompanies = mainStore.pageList
    if (listedCompanies == null || listedCompanies.length === 0) {
      return false
    }

    const companyInMainList = listedCompanies.find(company => company.id === sessionCompanyId)
    if (companyInMainList == null) {
      return false
    }

    if (
      sessionCompany.name != null &&
      sessionCompany.name.length > 0 &&
      companyInMainList.name !== sessionCompany.name
    ) {
      return false
    }

    headerStore.$patch({
      pageList: [...listedCompanies],
      pageMeta: mainStore.pageMeta,
      errors: []
    })

    isMultiPage.value = (mainStore.pageMeta?.lastPage ?? 1) > 1
    syncLocalCompaniesFromStore()
    syncPaginatedSearchFromStore()

    return true
  }

  async function fetchFirstPage (): Promise<boolean> {
    if (!isSessionRestoreComplete.value) {
      await restoreSessionOnce()
    }

    if (tryHydrateFromMainCompanyStore()) {
      return true
    }

    headerStore.errors = []

    const result = await Result.ofPromise(headerStore.getPage(PAGE, PER_PAGE))

    if (result.isError() || headerStore.errors.length > 0) {
      return false
    }

    isMultiPage.value = (pageMeta.value?.lastPage ?? 1) > 1
    syncLocalCompaniesFromStore()
    syncPaginatedSearchFromStore()

    return true
  }

  function ensureInitialLoad (): Promise<boolean> {
    const cached = getHeaderCompanySelectInitialLoadPromise()
    if (cached != null) {
      return cached
    }

    const promise = fetchFirstPage().then((loaded) => {
      if (!loaded) {
        clearHeaderCompanySelectInitialLoadPromise()
      }

      return loaded
    })

    setHeaderCompanySelectInitialLoadPromise(promise)

    return promise
  }

  function onFilter (value: string): void {
    paginatedSearch.onBeforeOpen()
    paginatedSearch.onFilter(value)
  }

  async function onDropdownHide (): Promise<boolean> {
    const hadSearch = (paginatedSearch.searchText.value?.trim().length ?? 0) > 0
    paginatedSearch.onClear()

    if (!hadSearch) {
      return true
    }

    headerStore.errors = []
    const result = await Result.ofPromise(headerStore.getPage(PAGE, PER_PAGE))

    if (result.isError() || headerStore.errors.length > 0) {
      return false
    }

    syncLocalCompaniesFromStore()
    syncPaginatedSearchFromStore()

    return true
  }

  return {
    selectOptions,
    isRemoteSearchLoading,
    isMultiPage,
    companies: pageList,
    pageMeta: pageMeta as HeaderCompanySelectSearch['pageMeta'],
    onFilter,
    onDropdownHide,
    ensureInitialLoad,
    findCompanyById
  }
}
